import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Eye, EyeOff, ChevronDown, Users, UserPlus, User, AlertCircle } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { SocialProfilePicture } from '../common/SocialProfilePicture';
import { Alert } from '../common/Alert';

interface TikTokComplianceCardProps {
  allowComments?: boolean;
  allowDuets?: boolean;
  allowStitch?: boolean;
  discloseContent?: boolean;
  privacy?: 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY' | '';
  disclosureType?: string[];
  tiktokUsername?: string;
  tiktokProfileImage?: string;
  userAgreedToTerms?: boolean;
  onAllowCommentsChange?: (allow: boolean) => void;
  onAllowDuetsChange?: (allow: boolean) => void;
  onAllowStitchChange?: (allow: boolean) => void;
  onPrivacyChange?: (privacy: 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY') => void;
  onDiscloseContentChange?: (disclose: boolean, type?: string) => void;
  onUserAgreedToTermsChange?: (agreed: boolean) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const TikTokComplianceCard: React.FC<TikTokComplianceCardProps> = ({
  allowComments = false,
  allowDuets = false,
  allowStitch = false,
  discloseContent = false,
  privacy = '',
  disclosureType = [],
  tiktokUsername = '',
  tiktokProfileImage = '',
  userAgreedToTerms = false,
  onAllowCommentsChange,
  onAllowDuetsChange,
  onAllowStitchChange,
  onPrivacyChange,
  onDiscloseContentChange,
  onUserAgreedToTermsChange,
  onValidationChange
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedPrivacy, setSelectedPrivacy] = React.useState(privacy);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Add a ref to track previous validation state to avoid redundant updates
  const prevValidationRef = useRef({
    isValid: false,
    triggered: false
  });

  // Update local state when props change
  React.useEffect(() => {
    console.log('🔍 TikTokComplianceCard - Privacy prop changed:', {
      newPrivacy: privacy,
      currentSelectedPrivacy: selectedPrivacy
    });
    if (privacy !== selectedPrivacy) {
      setSelectedPrivacy(privacy);
    }
  }, [privacy]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Log initial render and updates - remove this noisy logging
  React.useEffect(() => {
    console.log('🔍 TikTokComplianceCard - Component state:', {
      privacyProp: privacy,
      selectedPrivacyState: selectedPrivacy,
      isDropdownOpen: isOpen
    });
  }, [privacy, selectedPrivacy, isOpen]);

  // Get the privacy display text
  const getPrivacyDisplayText = () => {
    // Remove console logging
    if (!selectedPrivacy) {
      return t('tiktok.privacy.select');
    }
    return t(`tiktok.privacy.${selectedPrivacy.toLowerCase()}.label`);
  };

  // Get the privacy description text
  const getPrivacyDescriptionText = () => {
    // Remove console logging
    if (!selectedPrivacy) {
      return t('tiktok.privacy.selectDescription');
    }
    return t(`tiktok.privacy.${selectedPrivacy.toLowerCase()}.description`);
  };

  // Get the appropriate privacy icon
  const getPrivacyIcon = () => {
    if (!selectedPrivacy) return null;
    
    if (selectedPrivacy === 'PUBLIC_TO_EVERYONE') {
      return <Eye className="w-5 h-5 text-gray-400 mr-3" />;
    } else if (selectedPrivacy === 'MUTUAL_FOLLOW_FRIENDS') {
      return <Users className="w-5 h-5 text-gray-400 mr-3" />;
    } else if (selectedPrivacy === 'FOLLOWER_OF_CREATOR') {
      return <UserPlus className="w-5 h-5 text-gray-400 mr-3" />;
    } else {
      return <EyeOff className="w-5 h-5 text-gray-400 mr-3" />;
    }
  };

  // Handle privacy change with type checking
  const handlePrivacyChange = (privacyOption: string) => {
    console.log('🔍 Handling privacy change:', {
      from: selectedPrivacy,
      to: privacyOption
    });
    
    // Make sure the value is a valid privacy option before calling onPrivacyChange
    if (
      privacyOption === 'PUBLIC_TO_EVERYONE' ||
      privacyOption === 'MUTUAL_FOLLOW_FRIENDS' ||
      privacyOption === 'FOLLOWER_OF_CREATOR' ||
      privacyOption === 'SELF_ONLY'
    ) {
      const typedPrivacy = privacyOption as 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
      setSelectedPrivacy(typedPrivacy);
      onPrivacyChange?.(typedPrivacy);
    }
    setIsOpen(false);
  };

  // Handle branded content checkbox click
  const handleBrandedContentClick = () => {
    const willBeSelected = !disclosureType.includes('branded');
    if (willBeSelected && selectedPrivacy !== 'PUBLIC_TO_EVERYONE') {
      // Auto-switch to public visibility
      handlePrivacyChange('PUBLIC_TO_EVERYONE');
    }
    
    const newTypes = willBeSelected
      ? [...disclosureType, 'branded']
      : disclosureType.filter(t => t !== 'branded');
    onDiscloseContentChange?.(true, newTypes.join(','));
  };

  // Check if branded content option should be disabled - removed as we now allow clicking
  const isBrandedContentDisabled = false;

  // Add warning message when disclosure is enabled but no options are selected
  const showNoOptionsWarning = discloseContent && disclosureType.length === 0;
  
  // Remove the incomplete options warning as we only want a warning when no options are selected

  // Update validation state when disclosure content or types change
  React.useEffect(() => {
    // Disclosure validation: if disclosure is enabled, at least one option must be selected
    const isBrandDisclosureValid = !discloseContent || (discloseContent && disclosureType.length > 0);
    const isAgreedToTermsValid = userAgreedToTerms;
    const isPrivacyValid = !!selectedPrivacy;
    
    // Overall validation combines all validation checks
    const isValid = isBrandDisclosureValid && isAgreedToTermsValid && isPrivacyValid;
    
    // Only update and log if validation state changed or this is first trigger
    if (!prevValidationRef.current.triggered || prevValidationRef.current.isValid !== isValid) {
      // Update the ref
      prevValidationRef.current = {
        isValid,
        triggered: true
      };
      
      // Pass the validation state up to parent component
      onValidationChange?.(isValid);
      
      // Log validation state for debugging - only when it changes
      console.log('🔍 TikTokComplianceCard - Validation state changed:', {
        isBrandDisclosureValid,
        isAgreedToTermsValid,
        isPrivacyValid,
        overall: isValid,
        discloseContent,
        disclosureTypeCount: disclosureType.length
      });
    }
  }, [discloseContent, disclosureType, userAgreedToTerms, selectedPrivacy, onValidationChange]);

  // Function to determine which agreement text key to use
  const getAgreementKey = () => {
    const hasBrand = disclosureType.includes('brand');
    const hasBranded = disclosureType.includes('branded');

    if (hasBranded) {
      return 'tiktok.compliance.agreement.musicAndBranded'; // Branded always includes Music
    } else {
      return 'tiktok.compliance.agreement.musicOnly'; // Default to Music Only
    }
    // Note: 'brandedOnly' might not be reachable if Branded Content Policy implies Music Usage Confirmation
  };

  // Links for the Trans component
  const musicUsageLink = (
    <a 
      href={`https://www.tiktok.com/legal/page/global/music-usage-confirmation/${i18n.language}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-700"
    />
  );
  const brandedContentLink = (
    <a 
      href={`https://www.tiktok.com/legal/page/global/bc-policy/${i18n.language}`}
      target="_blank"
      rel="noopener noreferrer" 
      className="text-primary-600 hover:text-primary-700"
    />
  );

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('tiktok.compliance.title')}</h2>

      {/* TikTok Account Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('tiktok.compliance.publishOn', 'This content will be published on')}
        </label>
        <div className="p-3 bg-primary-50 rounded-lg border border-primary-500">
          <div className="flex items-center gap-3">
            <SocialProfilePicture
              pictureUrl={tiktokProfileImage}
              username={tiktokUsername || 'tiktokaccount'}
              platform="tiktok"
              size="md"
            />
            <div>
              <p className="font-medium text-gray-900">
                @{tiktokUsername || 'tiktokaccount'}
              </p>
              <p className="text-xs text-gray-500">TikTok</p>
            </div>
          </div>
        </div>
      </div>

      {/* Terms Agreement Checkbox */}
      <div className="mb-6">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="tiktok-terms-agreement"
            checked={userAgreedToTerms}
            onChange={() => onUserAgreedToTermsChange?.(!userAgreedToTerms)}
            className="h-4 w-4 rounded text-primary-600 border-gray-300 focus:ring-primary-500 mt-1"
            required
          />
          <label htmlFor="tiktok-terms-agreement" className="ml-3 mb-3">
            <span className="block text-sm font-medium text-gray-900">
              <Trans i18nKey={getAgreementKey()}
                components={[
                  brandedContentLink, // <0>
                  musicUsageLink      // <1>
                ]}
              />
            </span>
          </label>
        </div>
        {!userAgreedToTerms && (
          <Alert 
            type="warning" 
            message="tiktok.compliance.termsAgreementWarning" 
            size="sm"
            className="mt-2 text-xs"
          />
        )}
      </div>

      {/* Separator line */}
      <div className="border-t border-gray-200 mb-6"></div>

      <div className="mb-6 space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('tiktok.compliance.privacyQuestion')}
        </label>
        <div className="mt-1 relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full flex items-center justify-between p-3 rounded-lg border ${
              !selectedPrivacy ? 'border-gray-300 bg-white text-black' : 'border-gray-200 bg-white'
            } hover:bg-gray-50 transition-colors`}
          >
            <div className="flex items-center">
              {getPrivacyIcon()}
              <div className="text-left">
                <p className={`font-medium ${!selectedPrivacy ? 'text-black' : 'text-gray-900'}`}>
                  {getPrivacyDisplayText()}
                </p>
                <p className="text-sm text-gray-500">
                  {getPrivacyDescriptionText()}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {!selectedPrivacy && (
            <div className="mt-2 text-sm text-gray-600 flex items-center gap-1">
              <span>Please select a privacy option for TikTok</span>
            </div>
          )}

          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
              {['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR', 'SELF_ONLY'].map((privacyOption) => {
                const isDisabled = privacyOption === 'SELF_ONLY' && disclosureType.includes('branded');
                
                return (
                  <button
                    key={privacyOption}
                    type="button"
                    onClick={() => !isDisabled && handlePrivacyChange(privacyOption)}
                    className={`w-full flex items-start p-3 hover:bg-primary-50 transition-colors text-left 
                      ${selectedPrivacy === privacyOption ? 'bg-primary-50' : ''} 
                      ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isDisabled}
                  >
                    {privacyOption === 'PUBLIC_TO_EVERYONE' ? (
                      <Eye className="w-5 h-5 text-gray-400 mr-3" />
                    ) : privacyOption === 'MUTUAL_FOLLOW_FRIENDS' ? (
                      <Users className="w-5 h-5 text-gray-400 mr-3" />
                    ) : privacyOption === 'FOLLOWER_OF_CREATOR' ? (
                      <UserPlus className="w-5 h-5 text-gray-400 mr-3" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-gray-400 mr-3" />
                    )}
                    <div className="flex-grow">
                      <p className={`font-medium text-gray-900 ${isDisabled ? 'text-gray-400' : ''}`}> 
                        {t(`tiktok.privacy.${privacyOption.toLowerCase()}.label`)}
                      </p>
                      <p className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}> 
                        {t(`tiktok.privacy.${privacyOption.toLowerCase()}.description`)}
                      </p>
                      {privacyOption === 'SELF_ONLY' && (
                        <Alert 
                          type="warning"
                          message="tiktok.compliance.brandedContentPrivateWarning"
                          size="xs"
                          className="text-xs mt-1.5"
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Allow users to section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-4">{t('tiktok.compliance.allowUsersTo')}</h3>
          <div className="flex items-center space-x-6">
            {/* Comment Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('tiktok.compliance.comment')}</span>
              <button
                type="button"
                onClick={() => onAllowCommentsChange?.(!allowComments)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  allowComments ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    allowComments ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Duet Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('tiktok.compliance.duet')}</span>
              <button
                type="button"
                onClick={() => onAllowDuetsChange?.(!allowDuets)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  allowDuets ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    allowDuets ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Stitch Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('tiktok.compliance.stitch')}</span>
              <button
                type="button"
                onClick={() => onAllowStitchChange?.(!allowStitch)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  allowStitch ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    allowStitch ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Disclose video content section */}
        <div className="pt-4">
          <div className="flex items-center justify-between">
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">{t('tiktok.compliance.discloseTitle')}</h3>
                <button
                  type="button"
                  onClick={() => {
                    // If turning off disclosure, clear the types
                    if (discloseContent) {
                      onDiscloseContentChange?.(false, '');
                    } else {
                      onDiscloseContentChange?.(true, disclosureType.join(','));
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    discloseContent ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      discloseContent ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-500">
                {t('tiktok.compliance.discloseDescription')}
              </p>

              {discloseContent && (
                <div className="mt-4 space-y-4">
                  {/* Warning message when disclosure is enabled but no types selected */}
                  {showNoOptionsWarning && (
                    <Alert 
                      type="warning" 
                      message="tiktok.compliance.disclosureNoOptionsWarning" 
                      size="sm"
                      className="text-xs"
                    />
                  )}
                  
                  {/* Info Box - Show different messages based on selected disclosure types */}
                  {disclosureType.length > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-5 h-5 text-blue-400">
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-blue-700">
                        {disclosureType.includes('branded') 
                          ? "Your video will be labeled \"Paid Partnership\". This cannot be changed once your video is posted."
                          : disclosureType.includes('brand')
                            ? t('tiktok.compliance.infoBoxText')
                            : null}
                      </p>
                    </div>
                  )}

                  {/* Checkboxes */}
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div 
                        className="flex items-start cursor-pointer"
                        onClick={() => {
                          const newTypes = disclosureType.includes('brand')
                            ? disclosureType.filter(t => t !== 'brand')
                            : [...disclosureType, 'brand'];
                          onDiscloseContentChange?.(true, newTypes.join(','));
                        }}
                      >
                        <input
                          type="checkbox"
                          id="brand"
                          checked={disclosureType.includes('brand')}
                          onChange={() => {}} // Handled by parent div onClick
                          className="h-4 w-4 rounded text-primary-600 border-gray-300 focus:ring-primary-500 mt-1 cursor-pointer"
                        />
                        <label htmlFor="brand" className="ml-3 cursor-pointer">
                          <span className="block text-sm font-medium text-gray-900">{t('tiktok.compliance.yourBrand')}</span>
                          <span className="block text-sm text-gray-500">
                            {t('tiktok.compliance.yourBrandDescription')}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="flex items-start relative group">
                      <div 
                        className={`flex items-start ${selectedPrivacy === 'SELF_ONLY' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        onClick={() => {
                          if (selectedPrivacy !== 'SELF_ONLY') {
                            handleBrandedContentClick();
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          id="branded"
                          checked={disclosureType.includes('branded')}
                          disabled={selectedPrivacy === 'SELF_ONLY'}
                          onChange={() => {}} // Handled by parent div onClick
                          className={`h-4 w-4 rounded text-primary-600 border-gray-300 focus:ring-primary-500 mt-1 ${
                            selectedPrivacy === 'SELF_ONLY' ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        />
                        <label htmlFor="branded" className="ml-3 cursor-pointer">
                          <span className="block text-sm font-medium text-gray-900">{t('tiktok.compliance.brandedContent')}</span>
                          <span className="block text-sm text-gray-500">
                            {t('tiktok.compliance.brandedContentDescription')}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};